// Pole-aware polarity + rotating-field planning for the PCB stator. Pure module.
// Phase mapping stays c % numPhases. Three passes:
//   1. Per-coil flips align each phase's coil EMFs internally (winding factor).
//   2. Phase NORMALIZATION: reversing ALL coils of a phase shifts its EMF 180°
//      for free, so whole-phase flips are XORed in to realize a true
//      360/numPhases sequence when one is reachable (spec 2026-07-03 §1).
//      Fixes latent mis-phasing (e.g. 6c/10p raw resultants were 0/300/240).
//   3. Assessment: balanced = a rotating field exists (exact-sequence fit
//      within PHASE_SEPARATION_TOL_DEG after normalization, all phases alive).

export const PHASE_SEPARATION_TOL_DEG = 20;

export interface PolarityParams { numCoils: number; numPhases: number; numPoles: number; }
export interface PolarityPlan {
  flip: boolean[];
  windingFactorPerPhase: number[];
  phaseAngleDeg: number[];   // absolute resultant EMF phasor angle per phase
}

const TWO_PI = 2 * Math.PI;
/** Wrap an angle to (-PI, PI]. */
function wrapPi(a: number): number {
  let x = ((a % TWO_PI) + TWO_PI) % TWO_PI;
  if (x > Math.PI) x -= TWO_PI;
  return x;
}

export function planCoilPolarities(p: PolarityParams): PolarityPlan {
  const { numCoils, numPhases, numPoles } = p;
  const flip: boolean[] = new Array(numCoils).fill(false);
  const windingFactorPerPhase: number[] = [];
  const phaseAngle: number[] = [];          // radians, absolute
  const coilsByPhase: number[][] = [];

  const elec = (c: number) => c * (TWO_PI / numCoils) * (numPoles / 2);

  for (let ph = 0; ph < numPhases; ph++) {
    const phaseCoils: number[] = [];
    for (let c = ph; c < numCoils; c += numPhases) phaseCoils.push(c);
    coilsByPhase.push(phaseCoils);

    // Effective phasor before flipping: electrical angle + 180° when the
    // zig-zag chain traverses the coil backwards (odd position in the chain).
    const eff = (c: number, j: number) => elec(c) + (j % 2 === 1 ? Math.PI : 0);

    const ref = eff(phaseCoils[0], 0);
    let sumRe = 0, sumIm = 0;                // absolute-frame accumulation
    phaseCoils.forEach((c, j) => {
      const a = eff(c, j);
      flip[c] = Math.cos(a - ref) < -1e-9;   // >90° off → mirror the coil
      const final = a + (flip[c] ? Math.PI : 0);
      sumRe += Math.cos(final);
      sumIm += Math.sin(final);
    });
    windingFactorPerPhase.push(Math.hypot(sumRe, sumIm) / phaseCoils.length);
    phaseAngle.push(Math.hypot(sumRe, sumIm) < 1e-9 ? 0 : Math.atan2(sumIm, sumRe));
  }

  // ── Phase normalization ─────────────────────────────────────────────────
  // Search per-phase 180° offsets (phase 0 fixed) × both rotation directions
  // for the assignment closest to an exact ref + d·ph·step sequence. Mask
  // iteration starts at 0 (no extra flips) and replacement is strict <, so
  // the no-op wins ties → the default 6c/8p board stays byte-stable.
  const step = TWO_PI / numPhases;
  let bestMask = 0, bestMaxDev = Infinity;
  for (let mask = 0; mask < (1 << (numPhases - 1)); mask++) {
    const adj = phaseAngle.map((a, ph) =>
      ph > 0 && (mask & (1 << (ph - 1))) ? a + Math.PI : a);
    for (const d of [1, -1]) {
      let maxDev = 0;
      for (let ph = 1; ph < numPhases; ph++) {
        const dev = Math.abs(wrapPi(adj[ph] - (adj[0] + d * ph * step)));
        if (dev > maxDev) maxDev = dev;
      }
      if (maxDev < bestMaxDev - 1e-12) { bestMaxDev = maxDev; bestMask = mask; }
    }
  }
  const tol = PHASE_SEPARATION_TOL_DEG * Math.PI / 180;
  if (bestMask !== 0 && bestMaxDev <= tol) {
    for (let ph = 1; ph < numPhases; ph++) {
      if (bestMask & (1 << (ph - 1))) {
        for (const c of coilsByPhase[ph]) flip[c] = !flip[c];
        phaseAngle[ph] = wrapPi(phaseAngle[ph] + Math.PI);
      }
    }
  }

  return {
    flip,
    windingFactorPerPhase,
    phaseAngleDeg: phaseAngle.map(a => a * 180 / Math.PI),
  };
}

export interface RotatingFieldAssessment {
  balanced: boolean;
  phaseAnglesDeg: number[];
  minSeparationDeg: number;
}

export function assessRotatingField(p: PolarityParams): RotatingFieldAssessment {
  const plan = planCoilPolarities(p);
  const anglesRad = plan.phaseAngleDeg.map(a => a * Math.PI / 180);
  const tol = PHASE_SEPARATION_TOL_DEG * Math.PI / 180;
  const step = TWO_PI / p.numPhases;

  // Min pairwise circular separation (reporting).
  let minSep = Math.PI;
  for (let i = 0; i < anglesRad.length; i++) {
    for (let j = i + 1; j < anglesRad.length; j++) {
      const s = Math.abs(wrapPi(anglesRad[i] - anglesRad[j]));
      if (s < minSep) minSep = s;
    }
  }

  const allAlive = plan.windingFactorPerPhase.every(wf => wf > 0.1);
  // Post-normalization: balanced ⇔ the angles fit an exact sequence in one
  // of the two rotation directions within tolerance (180° freedom consumed).
  let fits = false;
  for (const d of [1, -1]) {
    let maxDev = 0;
    for (let ph = 1; ph < p.numPhases; ph++) {
      const dev = Math.abs(wrapPi(anglesRad[ph] - (anglesRad[0] + d * ph * step)));
      if (dev > maxDev) maxDev = dev;
    }
    if (maxDev <= tol) fits = true;
  }

  return {
    balanced: p.numPhases >= 3 && allAlive && fits,
    phaseAnglesDeg: plan.phaseAngleDeg,
    minSeparationDeg: (anglesRad.length > 1 ? minSep : 0) * 180 / Math.PI,
  };
}

export interface PoleOption { poles: number; windingFactor: number; balanced: boolean; }

/** Even pole counts 2..4*numCoils through the real planner. (2*numCoils is too
 *  narrow: for 6 coils it never reaches the 18/24-pole coincident cases the
 *  balance rule needs to exclude, nor the balanced 14/16/20/22-pole options —
 *  4*numCoils is the smallest round multiple that covers both.)
 *  Sorted: balanced first, then windingFactor desc, then |poles − numCoils| asc. */
export function recommendPoles(numCoils: number, numPhases: number): PoleOption[] {
  const opts: PoleOption[] = [];
  for (let poles = 2; poles <= 4 * numCoils; poles += 2) {
    const p = { numCoils, numPhases, numPoles: poles };
    const wf = Math.min(...planCoilPolarities(p).windingFactorPerPhase);
    opts.push({ poles, windingFactor: wf, balanced: assessRotatingField(p).balanced });
  }
  // windingFactor ties (e.g. multiple exactly-unity phase sets) can differ by
  // ~1e-16 float noise depending on which angles the trig lands on — compare
  // with a tolerance so noise doesn't leak into the ordering ahead of the
  // proximity tiebreak.
  const WF_TIE_EPS = 1e-9;
  opts.sort((a, b) =>
    Number(b.balanced) - Number(a.balanced) ||
    (Math.abs(b.windingFactor - a.windingFactor) > WF_TIE_EPS ? b.windingFactor - a.windingFactor : 0) ||
    Math.abs(a.poles - numCoils) - Math.abs(b.poles - numCoils));
  return opts;
}
